import type { Story } from '@/@types/story'
import type { Swiper as SwiperInstance } from 'swiper'
import { A11y, EffectCreative, Mousewheel } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css/effect-creative'
import StoryMediaFrame from './StoryMediaFrame'
import type { StoryMedia } from './storyMedia'
import { STORY_CREATIVE_EFFECT, STORY_WHEEL_OPTIONS, syncStorySlideMask } from './storySwiper'
import styles from './PersonalStoriesStage.module.css'

export interface StoryMediaSwiperProps {
	active: boolean
	activeStoryIndex: number
	stories: Story[]
	media?: StoryMedia
	hasNextPage: boolean
	interactionDisabled: boolean
	onStoryChange: (storyIndex: number) => void
	onLoadMore: () => Promise<boolean>
	onDoubleClickLike: () => void
	onTogglePlayback: () => void
	onToggleMuted: () => void
	onPlaybackBlocked: () => void
	onPlaybackTimeChange: (currentTime: number) => void
	onRetryMedia?: () => Promise<void>
	onMediaLoadingChange: (loading: boolean) => void
	onSwiper: (swiper: SwiperInstance) => void
}

export default function StoryMediaSwiper({
	active,
	activeStoryIndex,
	stories,
	media,
	hasNextPage,
	interactionDisabled,
	onStoryChange,
	onLoadMore,
	onDoubleClickLike,
	onTogglePlayback,
	onToggleMuted,
	onPlaybackBlocked,
	onPlaybackTimeChange,
	onRetryMedia,
	onMediaLoadingChange,
	onSwiper,
}: StoryMediaSwiperProps) {
	return (
		<div className={styles.mediaViewport}>
			<Swiper
				className={styles.storySwiper}
				modules={[A11y, EffectCreative, Mousewheel]}
				direction="vertical"
				effect="creative"
				creativeEffect={STORY_CREATIVE_EFFECT}
				initialSlide={activeStoryIndex}
				mousewheel={STORY_WHEEL_OPTIONS}
				speed={100}
				touchAngle={45}
				resistanceRatio={0.18}
				focusableElements="input, select, option, textarea, button, label"
				allowTouchMove={active && !interactionDisabled}
				watchSlidesProgress
				onSwiper={onSwiper}
				onProgress={syncStorySlideMask}
				onReachEnd={() => void onLoadMore()}
				onSlideChange={(swiper) => onStoryChange(swiper.activeIndex)}
			>
				{stories.map((story, index) => (
					<SwiperSlide key={story.id} className={styles.storySlide}>
						<StoryMediaFrame
							story={story}
							media={active && index === activeStoryIndex ? media : undefined}
							onTogglePlayback={onTogglePlayback}
							onToggleMuted={onToggleMuted}
							onPlaybackBlocked={onPlaybackBlocked}
							onPlaybackTimeChange={onPlaybackTimeChange}
							onRetry={onRetryMedia}
							onLoadingChange={onMediaLoadingChange}
							onDoubleClickLike={
								active && index === activeStoryIndex ? onDoubleClickLike : undefined
							}
						/>
					</SwiperSlide>
				))}
			</Swiper>
		</div>
	)
}
